import './index.scss';
import React from 'react';
import * as Color from 'color';
import { connect } from 'react-redux';
import { Button, Checkbox, Typography, Skeleton, Select, Tooltip, Layout, List, Input, Card, Alert } from 'antd';
import { PlusOutlined, MinusOutlined, CheckCircleFilled, CheckOutlined, CloseOutlined, LeftOutlined, DownOutlined } from '@ant-design/icons';
import { Service, ServiceGroup } from '../../types/service';
import { bookingActions } from '../../actions/booking';
import { User } from '../../types/user';
import { Stylist, StylistType } from '../../types/stylist';
import { countSelectedServices } from '../../utils/bookingUtil';
import getNested from '../../utils/getNested';

const { Option } = Select;
const { Text } = Typography;

interface Props {
  isAdmin: boolean;
  stylistId: number;
  skeleton?: boolean;
  primaryColor: string;

  maxServices?: number;
  serviceGroups?: ServiceGroup[];
  onChangeService?: (service: Service) => void;

  isFetchingStylists?: boolean;
  mappedStylists: Record<number, Stylist[]>;
  onChangeStylist?: (service: Service) => void;

  users: User[];
  activeUserId: number;
  activeRootCate: string;
  activeSubCate: string;
  storeActiveRootCate: (cateId: string) => void;
  storeActiveSubCate: (cateId: string) => void;
}

interface States {
  isMobileView: boolean;
  activeServiceKey?: string;
  activeServiceKeyword?: string;
}

class Services extends React.PureComponent<Props, States> {
  state: States;

  constructor(props) {
    super(props);
    this.state = {
      isMobileView: true,
      activeServiceKey: undefined,
      activeServiceKeyword: undefined,
    };
  }

  async componentDidMount() {
    this.onWindowResize();
    window.addEventListener('resize', () => this.onWindowResize());
  }

  componentWillUnmount() {
    window.removeEventListener('resize', () => this.onWindowResize());
  }

  private onWindowResize() {
    const isMobileView = window.innerWidth < 576;
    this.setState({ isMobileView });
  }

  private getAvailableStylists(selectedService: Service) {
    const { isAdmin, users, activeUserId, mappedStylists } = this.props;
    let excludedStylistIds: number[] = [];
    users
      .filter(user => {
        return user.id !== activeUserId && user.selectedServices.length > 0;
      })
      .forEach(user => {
        const matchedService = user.selectedServices.find(service => service.id === selectedService.id);
        if (matchedService && [StylistType.NoUser, StylistType.NoPreference].indexOf(matchedService.stylistId) === -1) {
          excludedStylistIds = excludedStylistIds.concat([matchedService.stylistId]);
        }
      });


    return (mappedStylists[activeUserId] || [])
      .filter(stylist => {
        const isBookAble = isAdmin || (stylist.canBook && !stylist.isOff);
        const isNoPreference = stylist.id === StylistType.NoPreference;
        const canPerform = isBookAble && stylist.availableServices.find(service => service.id === selectedService.id);

        return (isNoPreference || canPerform) && excludedStylistIds.indexOf(stylist.id) === -1;
      })
      ;
  }

  private getInvalidService() {
    const { users, activeUserId } = this.props;
    const activeUser = users.find(user => user.id === activeUserId) as User;
    const userServices = getNested(activeUser, 'selectedServices', []);
    return userServices.find((service: Service) => service.stylistId === StylistType.NoUser);
  }

  private openSelectServicesPopup(key?: string) {
    this.setState({
      activeServiceKey: key,
    });
  }

  private closeSelectServicesPopup() {
    this.setState({
      activeServiceKey: '',
      activeServiceKeyword: '',
    });
  }

  onChangeStylist(stylistId: string, selectedService: Service) {
    const { onChangeStylist } = this.props;
    const selectedStylist = this.getAvailableStylists(selectedService).find(stylist => stylist.id === Number(stylistId));

    if (selectedStylist && onChangeStylist) {
      onChangeStylist({ ...selectedService, stylistId: selectedStylist.id, stylistName: selectedStylist.name });
    }
  }

  onClickCategory(cate: ServiceGroup) {
    const {
      activeRootCate,
      activeSubCate,
      storeActiveRootCate,
      storeActiveSubCate,
    } = this.props;

    if (cate.type === 'category') {
      if (activeRootCate !== cate.key) {
        storeActiveRootCate(cate.key);
      } else {
        storeActiveRootCate('');
        storeActiveSubCate('');
      }
    } else {
      if (activeSubCate !== cate.key) {
        storeActiveSubCate(cate.key);
      } else {
        storeActiveSubCate('');
      }
    }
  }

  onChangeService(service: Service) {
    const { users, activeUserId, onChangeService } = this.props;

    if (onChangeService) {
      onChangeService(service);
    }

    const activeUser = users.find(user => user.id === activeUserId) as User;
    const userServices = getNested(activeUser, 'selectedServices', []);
    const selectedService = userServices.find(s => s.id === service.id);

    // auto open select stylist popup after selecting a service
    if (!selectedService) {
      this.openSelectServicesPopup(service.key);
    }
  }

  isCategoryExpanded(cate: ServiceGroup) {
    const { activeRootCate, activeSubCate } = this.props;
    const activeCateId = cate.type === 'category' ? activeRootCate : activeSubCate;

    return cate.key === activeCateId;
  }

  renderServiceList(serviceGroups: ServiceGroup[] = []) {
    const {
      users,
      isAdmin,
      stylistId,
      primaryColor,
      activeUserId,
      maxServices,
      isFetchingStylists,
    } = this.props;
    const activeUser = users.find(user => user.id === activeUserId) as User;
    const userServices = getNested(activeUser, 'selectedServices', []);

    // If there is only one group then render child services
    const hasOneSubCate = serviceGroups.length === 1 && serviceGroups[0].type === 'sub-category';
    const invalidService = this.getInvalidService();

    return serviceGroups.map((cate: any) => {
      if (cate.type !== 'service') {
        const serviceList = cate.data || [];
        const isExpanded = this.isCategoryExpanded(cate);
        const itemCount = countSelectedServices(activeUser, cate);

        return (
          <div
            className={`view view-services ${cate.type}`}
            key={`cate-${cate.key}`}
            style={{ marginBottom: 10 }}
          >
            {!hasOneSubCate && (
              <div className="view-header">
                <Button
                  className="view-services--cate"
                  type="primary"
                  style={{
                    backgroundColor: Color(primaryColor).alpha(0.75),
                    borderColor: 'transparent',
                  }}
                  disabled={invalidService && itemCount === 0}
                  onClick={() => this.onClickCategory(cate)}
                >
                  <span style={{ display: 'flex' }}>
                    {cate.name}
                    {itemCount > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', marginLeft: 20 }}>
                        <CheckCircleFilled style={{ marginRight: 6 }} /> {itemCount} {itemCount > 1 ? 'items' : 'item'}
                      </span>
                    )}
                  </span>
                  {isExpanded ? <MinusOutlined /> : <PlusOutlined /> }
                </Button>
              </div>
            )}

            {(isExpanded || hasOneSubCate) && (
              <div
                className="view-content"
                style={{
                  marginBottom: 16,
                }}
              >
                {this.renderServiceList(serviceList)}
              </div>
            )}
          </div>
        );
      }

      // render service
      const selectedService = userServices.find(service => service.id === cate.id);
      const isChecked = !!selectedService;
      let isDisabled = false;
      let tootipMessage;

      if ((!isChecked && invalidService && invalidService.id !== cate.id)) {
        isDisabled = true;
        tootipMessage = `Please select stylists for ${invalidService.name} to continue`;
      } else if (!isChecked && maxServices && maxServices > 0 && userServices.length >= maxServices) {
        isDisabled = true;
        tootipMessage = `You can only select max 2 services per client`;
      } else if ((activeUser.canSelectStylist && !cate.canPerform)) {
        isDisabled = true;
      }

      return (
        <div
          className={`view-services--item ${isChecked && activeUser.canSelectStylist ? 'with-stylists' : ''}`}
          key={`cate-${cate.key}`}
        >
          <Tooltip
            placement="top"
            className="view-services--item-tooltip"
            title={tootipMessage}
          >
            <Checkbox
              checked={isChecked}
              disabled={!!isDisabled}
              onChange={() => this.onChangeService(cate)}
            >
              <span>{cate.name}</span>
              {!isAdmin && !(isChecked && activeUser.canSelectStylist) && <span>${cate.price}{cate.isPriceUp ? '+' : ''} | {cate.duration}MIN</span>}
              {isAdmin && stylistId && activeUser.canSelectStylist && !cate.canPerform && (
                <Text type="danger">Cannot perform</Text>
              )}
            </Checkbox>
          </Tooltip>

          {isChecked && activeUser.canSelectStylist && (
            <div className="view-services--item-stylists">
              {isFetchingStylists && (!selectedService.stylistId || selectedService.stylistId === StylistType.NoUser) && (
                <Skeleton.Input />
              )}

              {(!isFetchingStylists || selectedService.stylistId !== StylistType.NoUser) && (
                <div className="view-services--stylists-container">
                  {!this.state.isMobileView && (
                    <Select
                      showSearch
                      autoFocus={!selectedService.stylistId || (selectedService.stylistId === StylistType.NoUser)}
                      defaultOpen={!selectedService.stylistId || (selectedService.stylistId === StylistType.NoUser)}
                      optionFilterProp="children"
                      placeholder="Select a stylist"
                      dropdownClassName="view-services--item-stylists-dropdown"
                      value={selectedService.stylistId !== StylistType.NoUser ? selectedService.stylistId : undefined}
                      onChange={stylistId => this.onChangeStylist(stylistId, cate)}
                      filterOption={(inputValue, option) => {
                        return String((option || {}).children || '').toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
                      }}
                      style={{ borderColor: Color(primaryColor).alpha(0.5) }}
                    >
                      {this.getAvailableStylists(cate).map(stylist => (
                        <Option
                          value={stylist.id}
                          key={`service${cate.id}-stylist-${stylist.id}-${Math.random()}`}
                        >{stylist.name}</Option>
                      ))}
                    </Select>
                  )}

                  {this.state.isMobileView && (
                    <>
                      <Card
                        style={{ borderColor: Color(primaryColor).alpha(0.5) }}
                        bodyStyle={{
                          backgroundColor: Color(primaryColor).alpha(0.15),
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          display: 'flex',
                          padding: '5px 12px',
                          cursor: 'pointer',
                        }}
                        onClick={() => this.openSelectServicesPopup(cate.key)}
                      >
                        <span
                          style={{ flexShrink: 1, flexGrow: 1 }}
                        >{selectedService.stylistName || 'Select a stylist'}</span> <DownOutlined
                          style={{ fontSize: 12 }}
                        />
                      </Card>

                      {this.state.activeServiceKey === selectedService.key && (
                        <div className="view-services--stylists-container--mobile">
                          <div className="view-services--all-stylists">
                            <Layout className="fave-layout fave-block">
                              <Layout.Header
                                className="fave-block--header"
                                style={{
                                  backgroundColor: 'transparent',
                                  color: 'inherit',
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: 16,
                                  borderBottom: '1px solid #f0f0f0',
                                }}
                              >
                                <Button
                                  icon={<LeftOutlined />}
                                  style={{
                                    border: 'none',
                                    position: 'relative',
                                    left: -16,
                                    boxShadow: 'none',
                                  }}
                                  onClick={!!selectedService.stylistId ? () => this.closeSelectServicesPopup() : undefined}
                                >back</Button>
                                <div style={{
                                  textAlign: 'center',
                                  flexGrow: 1,
                                  flexShrink: 1,
                                  position: 'relative',
                                  left: -16,
                                }}>
                                  <b>Select stylist</b>
                                </div>
                                <Button
                                  shape="circle"
                                  icon={<CloseOutlined style={{ fontSize: 16 }} />}
                                  style={{
                                    border: 'none',
                                    boxShadow: 'none',
                                  }}
                                  onClick={!!selectedService.stylistId ? () => this.closeSelectServicesPopup() : undefined}
                                />
                              </Layout.Header>

                              <Layout.Content
                                className="fave-block--content"
                                style={{ overflowY: 'auto', padding: 16 }}
                              >
                                {!selectedService.stylistId && (
                                  <div style={{ marginBottom: 16 }}>
                                    <Alert type="warning" message="Select a stylist to continue" />
                                  </div>
                                )}

                                <List
                                  itemLayout="horizontal"
                                  header={<div style={{ marginBottom: 16 }}>
                                    <Input
                                      size="large"
                                      placeholder="Enter a stylist name"
                                      onChange={evt => this.setState({ activeServiceKeyword: evt.target.value })}
                                    />
                                  </div>}
                                  dataSource={this.getAvailableStylists(cate).filter(stylist => {
                                    const { activeServiceKeyword } = this.state;
                                    return !!activeServiceKeyword
                                      ? stylist.name.toUpperCase().indexOf(activeServiceKeyword.toUpperCase()) !== -1
                                      : !!stylist.name;
                                  })}
                                  renderItem={stylist => {
                                    return (
                                      <List.Item
                                        actions={String(stylist.id) === String(selectedService.stylistId) ? [<CheckOutlined style={{ color: primaryColor }} />] : []}
                                        onClick={() => {
                                          this.onChangeStylist(String(stylist.id), cate);
                                          setTimeout(() => this.closeSelectServicesPopup(), 300);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <List.Item.Meta title={stylist.name} style={{ marginBottom: -4 }} />
                                      </List.Item>
                                    );
                                  }}
                                />
                              </Layout.Content>
                            </Layout>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  }

  renderSkeleton() {
    const fakeServices = [1, 2, 3, 4];
    return fakeServices.map(() => {
      return (
        <div className="view-services--item" key={`cate-${Math.random()}`}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            paddingBottom: 15,
            paddingTop: 15,
          }}>
            <div style={{ width: 24, marginRight: 16 }}>
              <Skeleton.Avatar size="small" shape="square" />
            </div>

            <Skeleton.Input size="small" />
          </div>
        </div>
      );
    });
  }

  render() {
    const { skeleton, serviceGroups } = this.props;
    return skeleton ? this.renderSkeleton() : this.renderServiceList(serviceGroups);
  }
}

const mapStateToProps = state => ({
  users: state.bookingStore.users,
  isAdmin: state.salonStore.isAdmin,
  stylistId: state.salonStore.stylistId,
  primaryColor: state.salonStore.primaryColor,
  activeUserId: state.bookingStore.activeUserId,
  activeRootCate: state.bookingStore.activeRootCate,
  activeSubCate: state.bookingStore.activeSubCate,
  mappedStylists: state.bookingStore.mappedStylists,
});
const mapDispatchToProps = dispatch => ({
  storeActiveRootCate: (cateId: string) => dispatch(bookingActions.storeActiveRootCate(cateId)),
  storeActiveSubCate: (cateId: string) => dispatch(bookingActions.storeActiveSubCate(cateId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Services);
